import { MenuExtraListModel } from '@/models/MenuExtraListModel';
import { MenuExtraListItemModel } from '@/models/MenuExtraListItemModel';
import { MenuItemModel } from '@/models/MenuItemModel';
import { MenuItemSizeModel } from '@/models/MenuItemSizeModel';

/**
 * Return requirement text, one of: optional, required, optional up to X (for example Up to 3)
 * or can be between X and Y (for example 2-4)
 * @param extraList: {MenuExtraListModel}
 * @return {string}
 */
export function requirementText(extraList: MenuExtraListModel): string {
  let requirement = 'Optional';
  let count = '';

  if (extraList.required) {
    requirement = 'Required';
  }

  if (extraList.max) {
    if (extraList.min) {
      if (extraList.min !== extraList.max) {
        count = `(Between ${extraList.min} and ${extraList.max})`;
      }
    } else {
      count = `(Up to ${extraList.max})`;
    }
  }

  return `${requirement} ${count}`;
}

/**
 * Return true if required to select one value. Otherwise return false
 * @param extraList: {MenuExtraListModel}
 * @return {boolean}
 */
export function isMultiple(extraList: MenuExtraListModel): boolean {
  return !(extraList.required && extraList.max === 1);
}

/**
 * Return maximum count of extras or undefined
 * @param extraList: {MenuExtraListModel}
 * @return {number | undefined}
 */
export function maxExtrasCount(extraList: MenuExtraListModel): number | undefined {
  return extraList.max || undefined;
}

/**
 * Return the count of selected extras
 * @param selectedExtras: {MenuExtraListItemModel[] | MenuExtraListItemModel}
 * @return {number}
 */
export function selectedExtrasCount(selectedExtras: MenuExtraListItemModel[] | MenuExtraListItemModel): number {
  return Array.isArray(selectedExtras) ? (selectedExtras as MenuExtraListItemModel[]).length : 1;
}

/**
 * Check if the order is valid
 * @param menuItem: {MenuItemModel}
 * @param selectedExtras: {Array<MenuExtraListItemModel[] | MenuExtraListItemModel | undefined>}
 * @param selectedSize: {MenuItemSizeModel | null}
 * @return {boolean}
 */
export function validateOrder(
  menuItem: MenuItemModel,
  selectedExtras: Array<MenuExtraListItemModel[] | MenuExtraListItemModel | undefined>,
  selectedSize: MenuItemSizeModel | null,
): boolean {
  let valid = true;

  if (!selectedSize) {
    valid = false;
    return valid;
  }

  menuItem.extraLists.forEach((e, i) => {
    if (e.min) {
      if (selectedExtras[i]) {
        const selectedExtra = selectedExtras[i] as MenuExtraListItemModel[] | MenuExtraListItemModel;
        if (selectedExtrasCount(selectedExtra) < e.min) {
          valid = false;
        }
      } else {
        valid = false;
      }
    }

    if (e.max && selectedExtras[i]) {
      const selectedExtra = selectedExtras[i] as MenuExtraListItemModel[] | MenuExtraListItemModel;
      if (selectedExtrasCount(selectedExtra) > e.max) {
        valid = false;
      }
    }
  });

  return valid;
}

/**
 * Calculate and return the string value of the order cost
 * @param selectedExtras: {Array<MenuExtraListItemModel[] | MenuExtraListItemModel | undefined>}
 * @param selectedSize: {MenuItemSizeModel | null}
 * @param count: {number}
 * @return {string}
 */
export function calculateOrderCost(
  selectedExtras: Array<MenuExtraListItemModel[] | MenuExtraListItemModel | undefined>,
  selectedSize: MenuItemSizeModel | null,
  count = 1,
): string {
  let price = selectedSize ? Number(selectedSize.price) : 0;

  selectedExtras.filter(Boolean).forEach((e) => {
    const selectedExtra = e as MenuExtraListItemModel[] | MenuExtraListItemModel;
    if (Array.isArray(selectedExtra)) {
      selectedExtra.forEach((l) => {
        price += Number(l.price);
      });
    } else {
      price += Number(selectedExtra.price);
    }
  });

  return (price * count).toFixed(2);
}
